import React from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import Home from "./pages/Home";
import ProtectedLayout from "./components/ProtectedLayout";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import OtpLogin from "./pages/OtpLogin";
import Register from "./pages/Register";
import RFIs from "./pages/RFIs";
import Submittals from "./pages/Submittals";
import SpecSections from "./pages/SpecSections";
import Vendors from "./pages/Vendors";
import Users from "./pages/Users";
import UserSettings from "./pages/UserSettings";
import PunchLists from "./pages/PunchLists";
import PunchListItems from "./pages/PunchListItems";
import DailyReports from "./pages/DailyReports";
import Safety from "./pages/Safety";
import PaymentSuccess from "./pages/PaymentSuccess";
import Workers from "./pages/Workers";
import DrawingFolders from "./pages/DrawingFolders";
import Drawings from "./pages/Drawings";
import MagicLinkHandler from "./components/MagicLinkHandler";
import Contracts from "./pages/Contracts";
import ChangeOrders from "./pages/ChangeOrders";
import ShopDrawings from "./pages/ShopDrawings";
import Documents from "./pages/Documents";
import Schedule from "./pages/Schedule";
import Tasks from "./pages/Tasks";
import OAuthConsent from "./pages/OAuthConsent";
import SharedFolder from "./pages/SharedFolder";

// Create a component to handle role-based route protection
const RoleProtectedRoute = ({ element, allowedRoles }) => {
  const { selectedProject } = useAuth();
  const userRole = selectedProject?.role;

  if (!userRole || !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return element;
};

// Define your routes
const router = createBrowserRouter([
  {
    path: "/login",
    element: <OtpLogin />,
  },
  {
    path: "/register",
    element: <Register />,
  },
  {
    path: "/magic-link",
    element: <MagicLinkHandler />,
  },
  {
    path: "/oauth/consent",
    element: <OAuthConsent />,
  },
  {
    path: "/shared/:kind/:shareToken",
    element: <SharedFolder />,
  },
  {
    path: "/",
    element: <ProtectedLayout />,
    children: [
      {
        path: "/",
        element: <Home />,
        index: true,
      },
      {
        path: "rfis",
        element: <RFIs />,
      },
      {
        path: "tasks",
        element: <Tasks />,
      },
      {
        path: "submittals",
        element: <Submittals />,
      },
      {
        path: "shop-drawings",
        element: <ShopDrawings />,
      },
      { path: "/punch-lists", element: <PunchLists /> },
      { path: "/punch-lists/:punchListId", element: <PunchListItems /> },
      {
        path: "/spec-sections",
        element: (
          <RoleProtectedRoute
            element={<SpecSections />}
            allowedRoles={["owner", "project_manager"]}
          />
        ),
      },
      {
        path: "/vendors",
        element: (
          <RoleProtectedRoute
            element={<Vendors />}
            allowedRoles={["owner", "project_manager"]}
          />
        ),
      },
      {
        path: "/users",
        element: (
          <RoleProtectedRoute
            element={<Users />}
            allowedRoles={["owner", "project_manager"]}
          />
        ),
      },
      {
        path: "/schedule",
        element: (
          <RoleProtectedRoute
            element={<Schedule />}
            allowedRoles={["owner", "project_manager", "field"]}
          />
        ),
      },
      {
        path: "contracts",
        //  loader: checkAuthLoader,
        element: (
          <RoleProtectedRoute
            element={<Contracts />}
            allowedRoles={["owner", "project_manager"]}
          />
        ),
      },
      {
        path: "change-orders",
        element: (
          <RoleProtectedRoute
            element={<ChangeOrders />}
            allowedRoles={["owner", "project_manager", "consultant", "subcontractor", "field"]}
          />
        ),
      },
      {
        path: "/daily-reports",
        element: (
          <RoleProtectedRoute
            element={<DailyReports />}
            allowedRoles={["owner", "project_manager", "field"]}
          />
        ),
      },
      {
        path: "/safety",
        element: (
          <RoleProtectedRoute
            element={<Safety />}
            allowedRoles={["owner", "project_manager", "field"]}
          />
        ),
      },
      {
        path: "/workers",
        element: (
          <RoleProtectedRoute
            element={<Workers />}
            allowedRoles={["owner", "project_manager"]}
          />
        ),
      },
      {
        path: "/drawing-folders",
        element: <DrawingFolders />,
      },
      {
        path: "/drawings/:folderId",
        element: <Drawings />,
      },
      {
        path: "/drawings/:folderId/:drawingId",
        element: <Drawings />,
      },
      {
        path: "/documents",
        element: <Documents />,
      },
      { path: "/settings", element: <UserSettings /> },
      { path: "/subscription/success", element: <PaymentSuccess /> },
    ],
  },
  // Catch-all route to redirect to home page
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

const App = () => {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
};

export default App;
